/**
 * Daily Dashboard Page
 * Tableau de bord quotidien avec KPIs de production
 */
import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import { Calendar, DollarSign, Users, Clock, TrendingUp, MapPin } from 'lucide-react';
import '../../../styles/admin/production/DailyDashboard.css';

const DailyDashboard = () => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedDate) {
      fetchDashboard();
    }
  }, [selectedDate]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/production/dashboard/daily?date=${selectedDate}`);
      setDashboardData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur chargement dashboard:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading-container">Chargement...</div>;
  }

  const kpis = dashboardData?.kpis || {};
  const routes = dashboardData?.routes || [];
  const employees = dashboardData?.employees || [];
  const clients = dashboardData?.clients || [];

  return (
    <div className="daily-dashboard-container">
      {/* Header */}
      <div className="dashboard-header">
        <div>
          <h1>Tableau de Bord Journalier</h1>
          <p className="subtitle">Analyse de performance et rentabilité</p>
        </div>
        <div className="date-selector">
          <Calendar size={20} />
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="date-input"
          />
        </div>
      </div>

      {/* KPIs Globaux */}
      <div className="kpi-section">
        <div className="kpi-card revenue">
          <div className="kpi-icon"><DollarSign size={28} /></div>
          <div className="kpi-content">
            <div className="kpi-label">Revenus Totaux</div>
            <div className="kpi-value">${kpis.total_revenue?.toFixed(2) || '0.00'}</div>
          </div>
        </div>

        <div className="kpi-card cost">
          <div className="kpi-icon"><TrendingUp size={28} /></div>
          <div className="kpi-content">
            <div className="kpi-label">Coûts Totaux</div>
            <div className="kpi-value">
              ${((kpis.total_labor_cost || 0) + (kpis.total_product_cost || 0)).toFixed(2)}
            </div>
            <div className="kpi-breakdown">
              MO: ${kpis.total_labor_cost?.toFixed(2) || '0.00'} • 
              Produits: ${kpis.total_product_cost?.toFixed(2) || '0.00'}
            </div>
          </div>
        </div>

        <div className="kpi-card profit">
          <div className="kpi-icon"><TrendingUp size={28} /></div>
          <div className="kpi-content">
            <div className="kpi-label">Profit</div>
            <div className={`kpi-value ${(kpis.total_profit || 0) >= 0 ? 'positive' : 'negative'}`}>
              ${kpis.total_profit?.toFixed(2) || '0.00'}
            </div>
          </div>
        </div>

        <div className="kpi-card margin">
          <div className="kpi-icon"><TrendingUp size={28} /></div>
          <div className="kpi-content">
            <div className="kpi-label">Marge</div>
            <div className={`kpi-value ${(kpis.margin_percent || 0) >= 0 ? 'positive' : 'negative'}`}>
              {kpis.margin_percent?.toFixed(1) || '0.0'}%
            </div>
          </div>
        </div>

        <div className="kpi-card clients">
          <div className="kpi-icon"><MapPin size={28} /></div>
          <div className="kpi-content">
            <div className="kpi-label">Clients Servis</div>
            <div className="kpi-value">{kpis.clients_served || 0}</div>
          </div>
        </div>

        <div className="kpi-card hours">
          <div className="kpi-icon"><Clock size={28} /></div>
          <div className="kpi-content">
            <div className="kpi-label">Heures Productives</div>
            <div className="kpi-value">{kpis.productive_hours?.toFixed(1) || '0.0'}h</div>
          </div>
        </div>
      </div>

      {/* Tableau Par Route */}
      <div className="table-section">
        <h2>Performance par Route</h2>
        <div className="table-container">
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>Numéro Route</th>
                <th>Service</th>
                <th>Clients</th>
                <th>Revenus</th>
                <th>Coûts</th>
                <th>Profit</th>
                <th>Marge</th>
              </tr>
            </thead>
            <tbody>
              {routes.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-data">
                    Aucune route complétée pour cette date
                  </td>
                </tr>
              ) : (
                routes.map((route, idx) => (
                  <tr key={idx}>
                    <td className="route-number">{route.route_number}</td>
                    <td>
                      <span className="service-badge">{route.service_type}</span>
                    </td>
                    <td>{route.clients}</td>
                    <td className="currency">${route.revenue?.toFixed(2)}</td>
                    <td className="currency">${route.cost?.toFixed(2)}</td>
                    <td className={`currency ${(route.profit || 0) >= 0 ? 'positive' : 'negative'}`}>
                      ${route.profit?.toFixed(2)}
                    </td>
                    <td className="margin">
                      <span className={(route.margin_percent || 0) >= 0 ? 'positive' : 'negative'}>
                        {route.margin_percent?.toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tableau Par Employé */}
      <div className="table-section">
        <h2>Performance par Employé</h2>
        <div className="table-container">
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>Employé</th>
                <th>Heures</th>
                <th>Taux Horaire</th>
                <th>Coût Total</th>
              </tr>
            </thead>
            <tbody>
              {employees.length === 0 ? (
                <tr>
                  <td colSpan="4" className="no-data">
                    Aucune donnée employé
                  </td>
                </tr>
              ) : (
                employees.map((emp, idx) => (
                  <tr key={idx}>
                    <td className="employee-name">{emp.name}</td>
                    <td>{emp.hours?.toFixed(1)}h</td>
                    <td className="currency">${emp.hourly_cost?.toFixed(2)}/h</td>
                    <td className="currency">${emp.total_cost?.toFixed(2)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tableau Par Client */}
      <div className="table-section">
        <h2>Performance par Client</h2>
        <div className="table-container">
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Service</th>
                <th>Durée</th>
                <th>Revenus</th>
                <th>Coûts</th>
                <th>Profit</th>
                <th>Marge</th>
              </tr>
            </thead>
            <tbody>
              {clients.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-data">
                    Aucun client servi
                  </td>
                </tr>
              ) : (
                clients.map((client, idx) => (
                  <tr key={idx}>
                    <td>{client.client_name}</td>
                    <td>
                      <span className="service-badge-small">{client.service_type}</span>
                    </td>
                    <td>{client.minutes} min</td>
                    <td className="currency">${client.revenue?.toFixed(2)}</td>
                    <td className="currency">${client.cost?.toFixed(2)}</td>
                    <td className={`currency ${(client.profit || 0) >= 0 ? 'positive' : 'negative'}`}>
                      ${client.profit?.toFixed(2)}
                    </td>
                    <td className="margin">
                      <span className={(client.margin_percent || 0) >= 0 ? 'positive' : 'negative'}>
                        {client.margin_percent?.toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DailyDashboard;
